import Close from '@mui/icons-material/Close'
import Visibility from '@mui/icons-material/Visibility'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import {
  getSupportOohBookableEvents,
  getSupportOohScreens,
  saveSupportOohConfig,
} from 'src/supportPortal/services/oohManagementApi'

const SUPPORTED_LOCAL_LANGUAGES = [
  { code: 'hi', label: 'Hindi' },
  { code: 'kn', label: 'Kannada' },
  { code: 'te', label: 'Telugu' },
]

const WALKIN_FEE_CURRENCY_PREFIX = {
  inr: 'INR ',
  usd: '$',
  eur: 'EUR ',
  gbp: 'GBP ',
}

const OOH_SCREEN_MODE_SINGLE = 'single-practitioner'
const OOH_SCREEN_MODE_TWO = 'two-practitioner'
const OOH_SCREEN_MODE_MULTI = 'multi-practitioner'

const OOH_SCREEN_MODE_OPTIONS = [
  { value: OOH_SCREEN_MODE_SINGLE, label: 'Primary only' },
  { value: OOH_SCREEN_MODE_TWO, label: 'Two practitioners' },
  { value: OOH_SCREEN_MODE_MULTI, label: 'Rotating multi' },
]

function normalizeOohScreenMode(value) {
  if (OOH_SCREEN_MODE_OPTIONS.some((option) => option.value === value)) {
    return value
  }
  return OOH_SCREEN_MODE_MULTI
}

function firstPresent(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '')
}

function formatWalkinEventFee(event) {
  const pricing = event?.pricing || event?.pricingDetail || event?.priceObject || event?.priceDetails || {}
  const fee = firstPresent(
    event?.discountedFee,
    event?.fee,
    event?.defaultPrice,
    pricing?.discountedFee,
    pricing?.fee,
    pricing?.price,
    pricing?.discountedPrice,
  )
  if (fee === undefined || fee === null || fee === '') return ''

  const numericFee = Number(fee)
  const amount = Number.isFinite(numericFee)
    ? new Intl.NumberFormat('en-IN', {
        maximumFractionDigits: Number.isInteger(numericFee) ? 0 : 2,
      }).format(numericFee)
    : String(fee)
  const currencyId = String(
    firstPresent(event?.currencyId, pricing?.currencyId, event?.defaultCurrency) || '',
  ).toLowerCase()
  const prefix = firstPresent(
    event?.currencySymbol,
    pricing?.currencySymbol,
    WALKIN_FEE_CURRENCY_PREFIX[currencyId],
    currencyId ? `${currencyId.toUpperCase()} ` : '',
  ) || ''
  return `${prefix}${amount}`
}

function formatWalkinEventSource(event) {
  if (event?.sourceLabel) return event.sourceLabel
  if (event?.sourceType === 'clinic-appointment') return 'Clinic appointment'
  if (event?.sourceType === 'personal') return 'Personal'
  return ''
}

function formatWalkinEventMode(event) {
  if (event?.meetingSrcLabel) return event.meetingSrcLabel
  if (event?.meetingSrc === 'offline') return 'In-Clinic'
  if (event?.meetingSrc === 'aumhum') return 'Video Call'
  return ''
}

function formatWalkinEventOptionLabel(event) {
  return [
    event?.title,
    event?.eventType || event?.type,
    formatWalkinEventMode(event),
    formatWalkinEventFee(event),
    formatWalkinEventSource(event),
  ]
    .filter(Boolean)
    .join(' - ')
}

// One editable practitioner OOH screen row.
function OohScreenRow({ screen, clinicLocationId, bookableEvents, eventsLoading, onPreview }) {
  const cfg = screen?.config || {}
  const [saving, setSaving] = useState(false)
  const [hideMentorDetails, setHideMentorDetails] = useState(Boolean(cfg.hideMentorDetails))
  const [displayLanguage, setDisplayLanguage] = useState(cfg.displayLanguage || '')
  const [announcementsEnabled, setAnnouncementsEnabled] = useState(cfg.announcementsEnabled !== false)
  const [walkinEnabled, setWalkinEnabled] = useState(Boolean(cfg.walkinBookingEnabled))
  const [defaultEventId, setDefaultEventId] = useState(cfg.walkinDefaultEventId || '')
  const [specialEventIds, setSpecialEventIds] = useState(
    Array.isArray(cfg.walkinSpecialEventIds) ? cfg.walkinSpecialEventIds : [],
  )
  const [qrAlwaysVisible, setQrAlwaysVisible] = useState(Boolean(cfg.walkinQrAlwaysVisible))
  const [screenMode, setScreenMode] = useState(normalizeOohScreenMode(cfg.screenMode))

  async function handleSave() {
    setSaving(true)
    try {
      const resp = await saveSupportOohConfig({
        mentorId: screen.mentorId,
        clinicLocationId,
        hideMentorDetails,
        displayLanguage: displayLanguage || '',
        screenMode,
        announcementsEnabled,
        walkinBookingEnabled: walkinEnabled,
        walkinDefaultEventId: walkinEnabled ? defaultEventId : '',
        walkinSpecialEventIds: walkinEnabled
          ? specialEventIds.filter((id) => id !== defaultEventId)
          : [],
        walkinQrAlwaysVisible: walkinEnabled ? qrAlwaysVisible : false,
      })
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        toast.success('OOH screen settings saved')
      } else {
        toast.error('Could not save. Please retry.')
      }
    } catch (e) {
      console.error('save ooh config failed', e)
      toast.error('Could not save. Please retry.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Accordion disableGutters sx={{ mb: 1, borderRadius: 2, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%', pr: 1 }}>
          <Typography sx={{ fontWeight: 600, flex: 1 }}>
            {screen?.mentorName || screen?.mentorId}
          </Typography>
          {screen?.hasConfig ? (
            <Chip
              size="small"
              color={cfg.walkinBookingEnabled ? 'success' : 'default'}
              label={cfg.walkinBookingEnabled ? 'Walk-in QR on' : 'Configured'}
            />
          ) : (
            <Chip size="small" variant="outlined" label="Not configured" />
          )}
          <Button
            size="small"
            startIcon={<Visibility />}
            onClick={(e) => {
              e.stopPropagation()
              onPreview(screen.mentorId)
            }}
          >
            Preview
          </Button>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          <FormControlLabel
            control={
              <Switch checked={hideMentorDetails} onChange={(e) => setHideMentorDetails(e.target.checked)} />
            }
            label="Hide practitioner photo/details on screen"
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 0.75 }}>
              Screen mode
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={screenMode}
              onChange={(_, value) => {
                if (value) setScreenMode(value)
              }}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 1,
                width: '100%',
                '& .MuiToggleButtonGroup-grouped': {
                  border: '1px solid #d1d5db !important',
                  borderRadius: '8px !important',
                  mx: '0 !important',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 1,
                  py: 0.85,
                  color: '#374151',
                  '&.Mui-selected': {
                    bgcolor: 'rgba(116, 80, 170, 0.12)',
                    color: '#5a3d88',
                    borderColor: '#7450aa !important',
                  },
                },
              }}
            >
              {OOH_SCREEN_MODE_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Local language (shown alongside English)
            </Typography>
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={displayLanguage || ''}
              onChange={(e) => setDisplayLanguage(e.target.value)}
            >
              <MenuItem value="">
                <em>English only</em>
              </MenuItem>
              {SUPPORTED_LOCAL_LANGUAGES.map((l) => (
                <MenuItem key={l.code} value={l.code}>
                  {l.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={announcementsEnabled}
                onChange={(e) => setAnnouncementsEnabled(e.target.checked)}
              />
            }
            label="Audio announcements (call patient by token)"
          />
          <FormControlLabel
            control={<Switch checked={walkinEnabled} onChange={(e) => setWalkinEnabled(e.target.checked)} />}
            label="Show a booking QR on this screen"
          />
          {walkinEnabled && (
            <Box sx={{ pl: 1 }}>
              {eventsLoading ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Loading bookable appointment types...
                  </Typography>
                </Stack>
              ) : bookableEvents.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  No bookable appointment types found for this practitioner at this location.
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mt: 1, mb: 0.5 }}>
                    Default appointment type (the QR books this)
                  </Typography>
                  <Select
                    size="small"
                    fullWidth
                    displayEmpty
                    value={defaultEventId || ''}
                    onChange={(e) => setDefaultEventId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select an appointment type</em>
                    </MenuItem>
                    {bookableEvents.map((ev) => (
                      <MenuItem key={ev?.uid} value={ev?.uid}>
                        {formatWalkinEventOptionLabel(ev)}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="body2" sx={{ mt: 1.5, mb: 0.5 }}>
                    Other appointment types the patient may also pick (optional)
                  </Typography>
                  {bookableEvents
                    .filter((ev) => ev?.uid !== defaultEventId)
                    .map((ev) => (
                      <FormControlLabel
                        key={ev?.uid}
                        control={
                          <Checkbox
                            checked={specialEventIds.includes(ev?.uid)}
                            onChange={(e) =>
                              setSpecialEventIds((prev) =>
                                e.target.checked
                                  ? [...prev, ev.uid]
                                  : prev.filter((id) => id !== ev.uid),
                              )
                            }
                          />
                        }
                        label={formatWalkinEventOptionLabel(ev)}
                      />
                    ))}
                  <FormControlLabel
                    sx={{ mt: 1 }}
                    control={
                      <Switch
                        checked={qrAlwaysVisible}
                        onChange={(e) => setQrAlwaysVisible(e.target.checked)}
                      />
                    }
                    label="Keep QR always visible (don't rotate it with other messages)"
                  />
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', ml: 6, mt: -0.5 }}>
                    When off, the QR appears as one rotating slide (~15s at a time) alongside other
                    display messages. When on, it stays pinned in its own card at all times.
                  </Typography>
                </>
              )}
            </Box>
          )}
          <Box sx={{ pt: 1 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || (walkinEnabled && !defaultEventId)}
            >
              {saving ? 'Saving…' : 'Save settings'}
            </Button>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

export default function OohScreensDrawer({ open, clinicLocation, onClose, onPreview }) {
  const clinicLocationId = clinicLocation?.id
  const [loading, setLoading] = useState(false)
  const [screens, setScreens] = useState([])
  const [eventsByMentor, setEventsByMentor] = useState({})
  const [eventsLoadingByMentor, setEventsLoadingByMentor] = useState({})

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!open || !clinicLocationId) return
      setLoading(true)
      setScreens([])
      setEventsByMentor({})
      setEventsLoadingByMentor({})
      try {
        const screensResp = await getSupportOohScreens(clinicLocationId)
        if (cancelled) return
        let loadedScreens = []
        if (screensResp?.status === 200 && isCommonResponseSuccessfulV2(screensResp)) {
          loadedScreens = getDataMapFromCommonResponse(screensResp)?.screens || []
          setScreens(loadedScreens)
        } else {
          setScreens([])
        }
        const mentorIds = loadedScreens.map((screen) => screen?.mentorId).filter(Boolean)
        setEventsLoadingByMentor(
          mentorIds.reduce((acc, mentorId) => ({ ...acc, [mentorId]: true }), {}),
        )
        if (!cancelled) setLoading(false)
        await Promise.all(
          mentorIds.map(async (mentorId) => {
            try {
              const eventsResp = await getSupportOohBookableEvents(clinicLocationId, mentorId)
              const events = getDataMapFromCommonResponse(eventsResp)?.events || []
              if (!cancelled) {
                setEventsByMentor((prev) => ({
                  ...prev,
                  [mentorId]: Array.isArray(events) ? events : [],
                }))
              }
            } catch (eventError) {
              console.error('load ooh bookable events failed', eventError)
              if (!cancelled) {
                setEventsByMentor((prev) => ({ ...prev, [mentorId]: [] }))
              }
            } finally {
              if (!cancelled) {
                setEventsLoadingByMentor((prev) => ({ ...prev, [mentorId]: false }))
              }
            }
          }),
        )
      } catch (e) {
        console.error('load ooh screens failed', e)
        if (!cancelled) {
          setScreens([])
          setEventsByMentor({})
          setEventsLoadingByMentor({})
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [open, clinicLocationId])

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 640, maxWidth: '95vw', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            p: 3,
            background: 'linear-gradient(135deg, #1a2332 0%, #243044 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              OOH / Token Screens
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {clinicLocation?.title || clinicLocation?.name}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : screens.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', p: 2 }}>
              No practitioners found at this location.
            </Typography>
          ) : (
            screens.map((screen) => (
              <OohScreenRow
                key={screen.mentorId}
                screen={screen}
                clinicLocationId={clinicLocationId}
                bookableEvents={eventsByMentor[screen.mentorId] || []}
                eventsLoading={Boolean(eventsLoadingByMentor[screen.mentorId])}
                onPreview={onPreview}
              />
            ))
          )}
        </Box>
      </Box>
    </Drawer>
  )
}
